import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import socketConnection, { getNewSocket } from './services/socketConnection';
import {
  Box, Paper, Stack, Group, Text, Title, Avatar, Button,
  TextInput, ActionIcon, Flex, Badge, RingProgress, UnstyledButton,
  Divider, Progress, Textarea, ThemeIcon, SimpleGrid, Modal, Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconSend, IconX, IconMessage, IconCheck, IconRefresh, IconUser,
  IconClock, IconPaperclip, IconMoodSmile, IconPhone, IconVideo,
  IconArrowLeft, IconChecklist, IconInfoCircle, IconMail, IconHelpCircle,
  IconBrandWhatsapp, IconBrandTelegram, IconShieldCheck, IconHeadset, IconPhoneOff,
  IconMicrophone, IconMicrophoneOff, IconStar, IconStarFilled,
} from '@tabler/icons-react';

// Use shared socket connection
const socket = socketConnection();

const COLORS = ['violet','blue','pink','teal','orange','green'];
const avatarColor = (name) => { let h=0; for(let c of (name||'')) h+=c.charCodeAt(0); return COLORS[h%COLORS.length]; };
const getInitial  = (name) => (name||'?').charAt(0).toUpperCase();
const formatTime  = (ts) => new Date(ts).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'});

/* ── Client Chat Page ── */
export default function ClientChatPage() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [step,         setStep]         = useState('idle'); // idle|info|queued|chatting|closed|rejected
  const [name,        setName]        = useState('');
  const [email,       setEmail]       = useState('');
  const [subject,     setSubject]     = useState('');
  const [description,  setDescription] = useState('');
  const [messages,    setMessages]    = useState([]);
  const [input,       setInput]       = useState('');
  const [queuePos,    setQueuePos]    = useState(null);
  const [queueTotal,  setQueueTotal]  = useState(null);
  const [closedMsg,   setClosedMsg]   = useState('');
  const [agentName,   setAgentName]   = useState('Support Agent');
  const [agentStatus, setAgentStatus] = useState('online');
  const [connectionError, setConnectionError] = useState(null);

  // Call state
  const [incomingCall, setIncomingCall] = useState(null); // { callType, from }
  const [callStatus,   setCallStatus]   = useState(null); // 'calling'|'connected'
  const [callModal,     setCallModal]   = useState(null); // 'audio'|'video'
  const [callRoomId,   setCallRoomId]   = useState('admin_room'); // Current call room
  const [localStream,   setLocalStream]  = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  // Rating state
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingError, setRatingError] = useState(null);

  const peerConnection = useRef(null);

  const messagesEnd = useRef(null);

  // Helper functions for localStorage session management
  const saveSessionToStorage = useCallback(() => {
    const sessionData = {
      roomId: localStorage.getItem('chat_roomId'),
      name,
      email,
      subject,
      description,
      messages,
      step,
      agentName,
      queuePos,
      queueTotal,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('chatSessionData', JSON.stringify(sessionData));
  }, [name, email, subject, description, messages, step, agentName, queuePos, queueTotal]);

  const restoreSessionFromStorage = useCallback(() => {
    const savedSession = localStorage.getItem('chatSessionData');
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);
        // Only restore if session is not closed or rejected
        if (sessionData.step !== 'closed' && sessionData.step !== 'rejected' && sessionData.step !== 'idle') {
          setName(sessionData.name || '');
          setEmail(sessionData.email || '');
          setSubject(sessionData.subject || '');
          setDescription(sessionData.description || '');
          setMessages(sessionData.messages || []);
          setStep(sessionData.step || 'idle');
          setAgentName(sessionData.agentName || 'Support Agent');
          setQueuePos(sessionData.queuePos || null);
          setQueueTotal(sessionData.queueTotal || null);
          console.log('Session restored from localStorage:', sessionData.step);
          return true;
        }
      } catch (error) {
        console.error('Error restoring session from storage:', error);
      }
    }
    return false;
  }, []);

  const clearSessionFromStorage = useCallback(() => {
    localStorage.removeItem('chat_roomId');
    localStorage.removeItem('chatSessionData');
  }, []);

  useEffect(() => {
    // Ensure socket is connected on mount
    if (!socket.connected) {
      console.log('Socket not connected on mount, connecting...');
      // The socket should auto-connect, but let's force it if needed
      socket.connect();
    }

    // Try to restore session from localStorage on mount
    const sessionRestored = restoreSessionFromStorage();

    // Listen for session restoration response
    socket.on('session:restored', (session) => {
      console.log('Session restored:', session);
      // Save the new roomId (updated to current socket.id by server)
      localStorage.setItem('chat_roomId', session.roomId);
      setName(session.userName || '');
      setEmail(session.email || '');
      setSubject(session.subject || '');

      if (session.status === 'queued') {
        setStep('queued');
        setQueuePos(1);
        setQueueTotal(1);
      } else if (session.status === 'active') {
        setStep('chatting');
        setAgentName(session.agentName || 'Support Agent');
        if (session.messages && session.messages.length > 0) {
          setMessages(session.messages.map(msg => ({
            ...msg,
            timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp
          })));
        }
        // Join the socket room to receive real-time messages
        socket.emit('user:join_room', { roomId: session.roomId });
      } else if (session.status === 'closed') {
        setStep('closed');
        setClosedMsg('Chat has been closed.');
      }
      saveSessionToStorage();
      // Clear connection error on successful session restore
      setConnectionError(null);
    });

    socket.on('session:not_found', () => {
      console.log('No existing session found on server');
      localStorage.removeItem('chat_roomId');
    });

    // Track connection status
    socket.on('connect', () => {
      console.log('Client: Socket connected');
      setConnectionError(null);

      // Try to restore previous session using saved roomId
      const savedRoomId = localStorage.getItem('chat_roomId');
      if (savedRoomId && savedRoomId !== socket.id) {
        console.log('Attempting to restore session with old roomId:', savedRoomId);
        socket.emit('user:restore_session', { roomId: savedRoomId });
      } else if (savedRoomId && savedRoomId === socket.id) {
        // Socket already has the correct ID, session is active
        console.log('Socket ID matches saved roomId, session is active');
      } else {
        // Save current socket.id as roomId for future reconnects
        localStorage.setItem('chat_roomId', socket.id);
      }
    });

    socket.on('disconnect', () => {
      console.log('Client: Socket disconnected');
      setConnectionError('Connection lost. Please refresh the page.');
    });

    socket.on('connect_error', (error) => {
      console.error('Client: Socket connection error:', error.message);
      setConnectionError('Cannot connect to server. Please check your network.');
    });

    socket.on('user:queued',         ({ position, total }) => { setStep('queued'); setQueuePos(position); setQueueTotal(total); });
    socket.on('user:queue_position', ({ position, total }) => { setQueuePos(position); setQueueTotal(total); });
    socket.on('user:accepted',       ({ agentName: agent }) => {
      setAgentName(agent || 'Support Agent');
      setStep('chatting');
      setMessages([{ sender:'system', text:`You are now connected with ${agent || 'a support agent'}. How can we help you today?`, timestamp:new Date().toISOString() }]);
    });
    socket.on('user:rejected',       ({ reason }) => { setStep('rejected'); setClosedMsg(reason||'Your request was dismissed.'); localStorage.removeItem('chat_roomId'); clearSessionFromStorage(); });
    socket.on('error', ({ message }) => {
      console.error('Server error:', message);
      setConnectionError(message);
      // If error indicates already in queue, go back to idle
      if (message && message.includes('already')) {
        setStep('idle');
      }
    });
    socket.on('user:message',        (msg) => {
      // Only add message if it's a valid object with text property
      if (msg && typeof msg === 'object' && typeof msg.text === 'string') {
        setMessages(prev=>[...prev, msg]);
        // Clear connection error when message arrives
        setConnectionError(null);
      }
    });
    socket.on('user:chat_closed',    ({ message }) => { setStep('closed'); setClosedMsg(message||'Chat closed by agent.'); localStorage.removeItem('chat_roomId'); clearSessionFromStorage(); });
    socket.on('user:agent_typing',   ({ isTyping }) => { setAgentStatus(isTyping ? 'typing' : 'online'); });

    // Call event listeners
    socket.on('call:incoming', ({ callType, from, callerSocketId }) => {
      console.log('[WebRTC Client] Incoming call from:', callerSocketId, 'type:', callType);
      setIncomingCall({ callType, from });
      setCallRoomId(callerSocketId); // store caller's socket ID so we can signal back
      setCallModal(callType); // Set callModal to determine video/audio
    });
    socket.on('call:accepted', ({ acceptedBy }) => {
      console.log('[WebRTC Client] Call accepted by:', acceptedBy);
      setCallRoomId(acceptedBy); // store who accepted so we can signal them
      setCallStatus('connected');
      setCallStartTime(Date.now());
      // Client initiated the call → client is the caller → client creates the offer
      createPeerConnectionAndOffer();
    });
    socket.on('call:ended', ({ duration }) => {
      const actualDuration = duration || (callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : 0);
      const minutes = Math.floor(actualDuration / 60);
      const seconds = actualDuration % 60;
      const durationStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

      // Add call ended message to chat
      if (actualDuration > 0) {
        const callEndMsg = {
          sender: 'system',
          text: `📞 Call ended - Duration: ${durationStr}`,
          timestamp: new Date().toISOString(),
          isCallEnded: true,
          callDuration: actualDuration
        };
        setMessages(prev => [...prev, callEndMsg]);
      }

      console.log('[WebRTC Client] Received call:ended, cleaning up...');

      // Clean up all call-related state
      cleanupCall();
      setCallStatus(null);
      setCallModal(null);
      setIncomingCall(null);
      setCallRoomId(null);  // Reset room ID - important!
      setCallStartTime(null);
      setCallDuration(0);
      setIsMuted(false);
    });
    socket.on('call:rejected', () => {
      setCallStatus(null);
      setCallModal(null);
      setIncomingCall(null);
      setCallRoomId(null);
      setIsMuted(false);
    });
    socket.on('call:offer', async ({ offer, from }) => {
      console.log('[WebRTC Client] Received offer from:', from);
      await handleOffer(offer, from);
    });
    socket.on('call:answer', async ({ answer }) => {
      console.log('[WebRTC Client] Received answer');
      await handleAnswer(answer);
    });
    socket.on('call:ice-candidate', async ({ candidate }) => {
      console.log('[WebRTC Client] Received ICE candidate');
      await handleIceCandidate(candidate);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('session:restored'); socket.off('session:not_found');
      socket.off('user:queued'); socket.off('user:queue_position');
      socket.off('user:accepted'); socket.off('user:rejected');
      socket.off('user:message'); socket.off('user:chat_closed');
      socket.off('user:agent_typing');
      socket.off('call:incoming'); socket.off('call:accepted'); socket.off('call:ended');
      socket.off('call:offer'); socket.off('call:answer'); socket.off('call:ice-candidate');
    };
  }, []);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior:'smooth' }); }, [messages, step]);

  // Save session to localStorage whenever state changes
  useEffect(() => {
    saveSessionToStorage();
  }, [name, email, subject, description, messages, step, agentName, queuePos, queueTotal, saveSessionToStorage]);

  // Rejoin chat room when entering chatting state or socket reconnects
  useEffect(() => {
    if (step === 'chatting' && socket.connected) {
      const savedRoomId = localStorage.getItem('chat_roomId');
      if (savedRoomId) {
        console.log('Rejoining chat room:', savedRoomId);
        socket.emit('user:rejoin_room', { roomId: savedRoomId });
        // Clear connection error when reconnecting to active chat
        setConnectionError(null);
      }
    }
  }, [step, socket.connected]);

  // Track socket connection state to update UI
  const [socketConnected, setSocketConnected] = useState(socket.connected);
  useEffect(() => {
    const handleConnect = () => setSocketConnected(true);
    const handleDisconnect = () => setSocketConnected(false);
    
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  const handleStartChat = () => { if (!name.trim()) return; setStep('info'); };

  const handleReconnect = () => {
    console.log('Manual reconnect requested');
    getNewSocket();
  };

  const handleSubmitToQueue = () => {
    if (!email.trim() || !subject.trim()) return;

    // Check if socket is connected before joining queue
    if (!socket.connected) {
      setConnectionError('Cannot connect to server. Please wait a moment and try again, or refresh the page.');
      return;
    }

    setStep('queued');
    socket.emit('user:join', { name, email, subject });
  };

  const handleInputChange = (val) => {
    setInput(val);
    if (step === 'chatting') socket.emit('user:typing', { text: val });
  };

  const sendMessage = () => {
    if (!input.trim() || step!=='chatting') return;
    const msg = { sender:'user', text:input, timestamp:new Date().toISOString() };
    setMessages(prev=>[...prev, msg]);
    socket.emit('user:message', { message:input });
    socket.emit('user:typing', { text: '' });
    setInput('');
  };

  const handleEndChat = () => {
    // Show confirmation to user
    if (window.confirm('Are you sure you want to end this conversation?')) {
      socket.emit('user:end_chat', {});
      setStep('closed');
      setClosedMsg('You have ended the conversation. Thank you for contacting us!');
      clearSessionFromStorage();
    }
  };

  const reset = () => { clearSessionFromStorage(); setStep('idle'); setName(''); setEmail(''); setSubject(''); setDescription(''); setMessages([]); setInput(''); setQueuePos(null); setQueueTotal(null); setClosedMsg(''); setAgentName('Support Agent'); setConnectionError(null); setRating(0); setFeedback(''); setRatingSubmitted(false); setRatingLoading(false); setRatingError(null); };

  const submitRating = async () => {
    if (rating < 1) return;
    
    setRatingLoading(true);
    setRatingError(null);
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004/api';
      // Use the roomId from localStorage (where it was saved on connect)
      const roomId = localStorage.getItem('chat_roomId') || socket.id;
      
      console.log(`[Rating] Submitting rating ${rating} to ${API_URL}/chat-sessions/${roomId}/rate`);
      
      const response = await fetch(`${API_URL}/chat-sessions/${roomId}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, feedback })
      });
      
      if (response.ok) {
        console.log('[Rating] Rating submitted successfully');
        setRatingSubmitted(true);
        setTimeout(() => reset(), 2000); // Auto reset after 2 seconds
      } else {
        const errorData = await response.text();
        console.error('[Rating] API error:', response.status, errorData);
        setRatingError(`Failed to submit rating (${response.status}). Please try again.`);
        setRatingLoading(false);
      }
    } catch (err) {
      console.error('[Rating] Network error:', err);
      setRatingError(`Connection error: ${err.message}. Please check your internet connection and try again.`);
      setRatingLoading(false);
    }
  };

  // ── WebRTC Call Functions ──
  // ICE candidate buffer — candidates that arrive before remoteDescription is set
  const iceCandidateBuffer = useRef([]);

  // Helper: get the socket ID of the remote peer
  const getTargetSocketId = () => callRoomId;

  // Helper: create a new RTCPeerConnection with common config
  const createPeerConnectionHelper = (targetSocketId) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ]
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[WebRTC Client] ICE candidate generated');
        socket.emit('call:ice-candidate', { targetSocketId, candidate: event.candidate });
      }
    };

    pc.ontrack = (event) => {
      console.log('[WebRTC Client] Remote track received:', event.track.kind);
      setRemoteStream(event.streams[0]);
    };

    pc.onconnectionstatechange = () => {
      console.log('[WebRTC Client] Connection state:', pc.connectionState);
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC Client] ICE connection state:', pc.iceConnectionState);
    };

    peerConnection.current = pc;
    return pc;
  };

  // Helper: get local media stream
  const getLocalMedia = async (isVideo) => {
    console.log('[WebRTC Client] Requesting media, video:', isVideo);
    try {
      // First, check if media devices are available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported in this browser');
      }

      // Get available devices first to check permissions
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      console.log('[WebRTC Client] Available audio input devices:', audioInputs.length);

      if (audioInputs.length === 0) {
        throw new Error('No microphone found on this device');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: isVideo,
        audio: true,
      });
      console.log('[WebRTC Client] Got local stream, tracks:', stream.getTracks().map(t => t.kind));

      // Verify audio track is enabled
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('[WebRTC Client] Audio track enabled:', audioTrack.enabled, 'label:', audioTrack.label);
      }

      setLocalStream(stream);
      return stream;
    } catch (err) {
      console.error('[WebRTC Client] getUserMedia error:', err.name, err.message);
      throw err;
    }
  };

  // Called when client is the CALLER (client initiated call, admin accepted)
  const createPeerConnectionAndOffer = async () => {
    try {
      const target = getTargetSocketId();
      console.log('[WebRTC Client] Creating offer for target:', target);
      const isVideo = callModal === 'video';
      const stream = await getLocalMedia(isVideo);
      const pc = createPeerConnectionHelper(target);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log('[WebRTC Client] Sending offer to:', target);
      socket.emit('call:offer', { targetSocketId: target, offer });
    } catch (err) {
      console.error('[WebRTC Client] Error creating offer:', err);
      handleMediaError(err);
    }
  };

  // Called when client receives an offer (client is the CALLEE)
  const handleOffer = async (offer, fromSocketId) => {
    try {
      const target = fromSocketId || getTargetSocketId();
      if (!callRoomId) setCallRoomId(target);

      console.log('[WebRTC Client] Handling offer from:', target);
      console.log('[WebRTC Client] callModal:', callModal, 'incomingCall:', incomingCall);

      // Determine if this is a video call - use callModal which is set from call:incoming
      // Note: incomingCall may be null at this point because acceptCall() clears it
      const isVideo = callModal === 'video';
      console.log('[WebRTC Client] isVideo:', isVideo, '(using callModal only, incomingCall may be null after acceptCall)');

      const stream = await getLocalMedia(isVideo);
      const pc = createPeerConnectionHelper(target);
      stream.getTracks().forEach(track => {
        console.log('[WebRTC Client] Adding track to PC:', track.kind, 'enabled:', track.enabled);
        pc.addTrack(track, stream);
      });

      await pc.setRemoteDescription(new RTCSessionDescription(offer));

      // Flush buffered ICE candidates
      while (iceCandidateBuffer.current.length > 0) {
        const buffered = iceCandidateBuffer.current.shift();
        await pc.addIceCandidate(new RTCIceCandidate(buffered));
        console.log('[WebRTC Client] Flushed buffered ICE candidate');
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log('[WebRTC Client] Sending answer to:', target);
      socket.emit('call:answer', { targetSocketId: target, answer });
    } catch (err) {
      console.error('[WebRTC Client] Error handling offer:', err);
      handleMediaError(err);
    }
  };

  const handleAnswer = async (answer) => {
    if (peerConnection.current) {
      console.log('[WebRTC Client] Setting remote description from answer');
      await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
      // Flush buffered ICE candidates
      while (iceCandidateBuffer.current.length > 0) {
        const buffered = iceCandidateBuffer.current.shift();
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(buffered));
        console.log('[WebRTC Client] Flushed buffered ICE candidate');
      }
    }
  };

  const handleIceCandidate = async (candidate) => {
    if (peerConnection.current && peerConnection.current.remoteDescription) {
      try {
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('[WebRTC Client] Error adding ICE candidate:', error);
      }
    } else {
      console.log('[WebRTC Client] Buffering ICE candidate (no remote description yet)');
      iceCandidateBuffer.current.push(candidate);
    }
  };

  const handleMediaError = (err) => {
    console.error('[WebRTC Client] Media error details:', err);
    let errorMessage = '';

    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      errorMessage = 'Camera/Microphone access denied. Please allow camera and microphone permissions in your browser settings and try again.';
    } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      errorMessage = 'No camera or microphone found. Please connect a device and try again.';
    } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
      errorMessage = 'Camera or microphone is already in use by another application. Please close other apps using your camera/mic and try again.';
    } else if (err.name === 'OverconstrainedError') {
      errorMessage = 'Your camera or microphone does not support the required settings. Please check your device settings.';
    } else {
      errorMessage = `Failed to start call: ${err.message || 'Unknown error'}. Please check your camera and microphone settings.`;
    }

    alert(errorMessage);
    console.log('[WebRTC Client] Showing error alert to user');
    cleanupCall();
    setCallStatus(null);
    setCallModal(null);
  };

  const acceptCall = () => {
    const callType = incomingCall?.callType;
    const target = callRoomId; // callerSocketId stored when call:incoming was received
    console.log('[WebRTC Client] Accepting call from:', target);
    socket.emit('call:accept', { targetSocketId: target });
    setCallStatus('connected');
    setCallModal(callType || 'audio');
    setIncomingCall(null);
    // Don't create offer here — wait for the caller's offer via call:offer event
  };

  const rejectCall = () => {
    socket.emit('call:reject', { targetSocketId: callRoomId });
    setIncomingCall(null);
    setCallRoomId(null);
    iceCandidateBuffer.current = [];
  };

  // Cleanup media & peer connection without emitting socket events
  const cleanupCall = () => {
    console.log('[WebRTC Client] Cleaning up call');

    // Stop all local stream tracks
    if (localStream) {
      localStream.getTracks().forEach(track => {
        console.log('[WebRTC Client] Stopping local track:', track.kind);
        track.stop();
      });
      setLocalStream(null);
    }

    // Close peer connection and stop all sender tracks
    if (peerConnection.current) {
      peerConnection.current.getSenders().forEach(sender => {
        if (sender.track) {
          console.log('[WebRTC Client] Stopping sender track:', sender.track.kind);
          sender.track.stop();
        }
      });
      peerConnection.current.close();
      peerConnection.current = null;
    }

    // Stop remote stream tracks
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => {
        console.log('[WebRTC Client] Stopping remote track:', track.kind);
        track.stop();
      });
      setRemoteStream(null);
    }

    iceCandidateBuffer.current = [];
    console.log('[WebRTC Client] Call cleanup complete');
  };

  const endCall = () => {
    const target = getTargetSocketId();
    const duration = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : callDuration;
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    const durationStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    // Add call ended message to chat
    if (duration > 0) {
      const callEndMsg = {
        sender: 'system',
        text: `📞 Call ended - Duration: ${durationStr}`,
        timestamp: new Date().toISOString(),
        isCallEnded: true,
        callDuration: duration
      };
      setMessages(prev => [...prev, callEndMsg]);
    }

    socket.emit('call:end', { targetSocketId: target, duration });
    cleanupCall();
    setCallStatus(null);
    setCallModal(null);
    setCallRoomId(null);
    setCallStartTime(null);
    setCallDuration(0);
    setIsMuted(false);
  };

  const initiateCall = (callType) => {
    console.log('[WebRTC Client] Initiating call:', { callType, socketId: socket.id });
    setCallModal(callType);
    setCallRoomId(null); // will be set when call:accepted arrives with acceptedBy
    setCallStatus('calling');
    socket.emit('call:initiate', { roomId: 'admin_room', callType, clientSocketId: socket.id });
  };

  const headerColor = step==='chatting' ? 'green' : step==='queued' ? 'orange' : step==='closed'||step==='rejected' ? 'red' : 'blue';
  const headerStatus = step==='queued' ? `Waiting in queue (#${queuePos})` : step==='chatting' ? 'Connected with agent' : step==='closed' ? 'Chat ended' : step==='rejected' ? 'Request dismissed' : 'Start a conversation';

  // Mobile-responsive container
  const containerWidth = isMobile ? '100%' : 480;
  const containerHeight = isMobile ? '100vh' : 'auto';
  const containerMaxHeight = isMobile ? '100vh' : '700px';

  return (
    <Box style={{
      minHeight:'100vh',
      background: '#fff8f0',
      fontFamily:'Inter, Segoe UI, sans-serif',
      padding: isMobile ? '0' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Main Container */}
      <Paper shadow={isMobile ? 'none' : 'xl'}
        style={{
          width: containerWidth,
          height: containerHeight,
          maxHeight: containerMaxHeight,
          borderRadius: isMobile ? '0' : '24px',
          overflow:'hidden',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
        }}>

        {/* Header */}
        <Box px={isMobile ? 'md' : 'xl'} py={isMobile ? 'sm' : 'md'}
          style={{
            background:`linear-gradient(135deg, #ff8c00 0%, #ffb347 100%)`,
            flexShrink: 0
          }}>
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon size={isMobile ? 36 : 44} radius="xl" variant="white" color="orange">
                <IconHeadset size={isMobile ? 18 : 22}/>
              </ThemeIcon>
              <Box>
                <Title order={isMobile ? 5 : 4} c="#2d2d2d">Customer Support</Title>
                <Group gap={6}>
                  <Badge size="xs" color={step==='chatting'?'green':step==='queued'?'orange':'white'} variant={step==='idle'?'filled':'white'} leftSection={step==='chatting'?<IconCheck size={10}/>:<IconClock size={10}/>}>
                    {headerStatus}
                  </Badge>
                  {/* Connection status indicator */}
                  <Tooltip label={socketConnected ? 'Connected to server' : 'Disconnected - click to reconnect'}>
                    <Badge
                      size="xs"
                      color={socketConnected ? 'green' : 'red'}
                      variant="white"
                      style={{ cursor: socketConnected ? 'default' : 'pointer' }}
                      onClick={() => {
                        if (!socketConnected) {
                          handleReconnect();
                        }
                      }}
                    >
                      {socketConnected ? '● Online' : '○ Offline'}
                    </Badge>
                  </Tooltip>
                </Group>
              </Box>
            </Group>
            {/* <Group gap={4}>
              {step!=='idle' && step!=='info' && (
                <ActionIcon variant="white" color="dark" onClick={reset}><IconRefresh size={18}/></ActionIcon>
              )}
              <ActionIcon variant="white" color="dark" onClick={reset} title="Close chat">
                <IconX size={18}/>
              </ActionIcon>
            </Group> */}
          </Group>
        </Box>

        {/* Main Content */}
        <Box style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column' }}>

        {/* Step 1: Welcome */}
        {step==='idle' && (
          <Flex direction="column" align="center" justify="center" p={isMobile ? 'lg' : 'xl'} gap="lg" style={{ flex:1, overflow:'auto' }}>
            <Box style={{ width:90, height:90, borderRadius:'50%', background:'linear-gradient(135deg, #ff8c00 0%, #ffb347 100%)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 10px 40px rgba(255,140,0,0.4)' }}>
              <IconMessage size={44} color="#2d2d2d"/>
            </Box>
            <Title order={2} ta="center" c="dark.5" fw={700}>Welcome! 👋</Title>
            <Text c="dimmed" ta="center" maw={isMobile ? '100%' : 350} size="md">
              Chat with our friendly support team. We're here to help you with any questions or issues.
            </Text>

            {/* Quick help options */}
            <SimpleGrid cols={2} spacing="sm" w={isMobile ? '100%' : 320} mt="md">
              <Paper p="sm" withBorder radius="md" style={{ cursor:'pointer', borderColor:'#e9ecef', transition:'all 0.2s' }}>
                <Stack gap={4} align="center">
                  <IconHelpCircle size={24} color="#ff8c00"/>
                  <Text size="xs" fw={600} ta="center">Help Center</Text>
                </Stack>
              </Paper>
              <Paper p="sm" withBorder radius="md" style={{ cursor:'pointer', borderColor:'#e9ecef', transition:'all 0.2s' }}>
                <Stack gap={4} align="center">
                  <IconBrandWhatsapp size={24} color="#25D366"/>
                  <Text size="xs" fw={600} ta="center">WhatsApp</Text>
                </Stack>
              </Paper>
            </SimpleGrid>

            <Stack w={isMobile ? '100%' : 320} gap="sm" mt="lg">
              <TextInput
                placeholder="What should we call you?"
                size="md"
                radius="md"
                leftSection={<IconUser size={18}/>}
                styles={{ input: { '&:focus': { borderColor:'#ff8c00' } } }}
                value={name}
                onChange={e=>setName(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleStartChat()}
              />
              <Button fullWidth size="md" radius="md" variant="gradient" gradient={{from:'#ff8c00',to:'#ffb347'}} onClick={handleStartChat}
                style={{ boxShadow:'0 4px 15px rgba(255,140,0,0.4)' }}>
                Get Started →
              </Button>
            </Stack>
            <Group mt="md">
              <IconShieldCheck size={14} color="#10b981"/>
              <Text size="xs" c="dimmed">Secure & Private • Typically replies in 2 min</Text>
            </Group>
          </Flex>
        )}

        {/* Step 2: Info Form */}
        {step==='info' && (
          <Flex direction="column" align="center" p={isMobile ? 'md' : 'xl'} gap="lg" style={{ flex:1, overflow:'auto' }}>
            <Group gap="sm" w="100%" maw={320}>
              <ActionIcon variant="light" onClick={()=>setStep('idle')}><IconArrowLeft size={18}/></ActionIcon>
              <Title order={5} fw={600}>Tell us about your issue</Title>
            </Group>
            <Text c="dimmed" ta="center" size="sm">Help us prepare to assist you better</Text>

            <Stack w={isMobile ? '100%' : 320} gap="md">
              <TextInput
                placeholder="Your email address"
                size="md"
                radius="md"
                leftSection={<IconMail size={18}/>}
                value={email}
                onChange={e=>setEmail(e.target.value)}
              />
              <TextInput
                placeholder="What can we help you with?"
                size="md"
                radius="md"
                leftSection={<IconHelpCircle size={18}/>}
                value={subject}
                onChange={e=>setSubject(e.target.value)}
              />
              <Textarea
                placeholder="Tell us more details (optional)"
                size="md"
                radius="md"
                minRows={3}
                value={description}
                onChange={e=>setDescription(e.target.value)}
              />
              <Button fullWidth size="md" radius="md" variant="gradient" gradient={{from:'#ff8c00',to:'#ffb347'}} onClick={handleSubmitToQueue}
                disabled={!email || !subject}
                style={{ boxShadow:'0 4px 15px rgba(255,140,0,0.4)' }}>
                Join Queue →
              </Button>
            </Stack>
          </Flex>
        )}

        {/* Step 3: Queue */}
        {step==='queued' && (
          <Flex direction="column" align="center" justify="center" gap="lg" p={isMobile ? 'md' : 'xl'} style={{ flex:1 }}>
            <RingProgress size={130} thickness={8} roundCaps
              sections={[{ value: queueTotal>0 ? ((queueTotal-queuePos+1)/queueTotal)*100 : 50, color:'#ff8c00' }]}
              label={
                <Stack gap={0} align="center">
                  <Text size="xl" fw={700}>#{queuePos}</Text>
                  <Text size="xs" c="dimmed">in queue</Text>
                </Stack>
              }
            />
            <Title order={4} ta="center" fw={600}>You're in the queue</Title>
            <Text c="dimmed" ta="center" maw={280} size="sm">
              There are {queueTotal - 1} people ahead of you. We'll notify you when an agent is available.
            </Text>
            <Paper p="md" radius="md" withBorder w={isMobile ? '100%' : 280}>
              <Group justify="space-between">
                <Box>
                  <Text size="xs" c="dimmed">Your name</Text>
                  <Text fw={600}>{name}</Text>
                </Box>
                <Box ta="right">
                  <Text size="xs" c="dimmed">Topic</Text>
                  <Text fw={600} lineClamp={1} maw={120}>{subject}</Text>
                </Box>
              </Group>
            </Paper>
            <Button variant="subtle" color="red" mt="md" onClick={reset}>Cancel Request</Button>
          </Flex>
        )}

        {/* Step 4: Chatting */}
        {step==='chatting' && (
          <Flex direction="column" style={{ flex:1, overflow:'hidden' }}>
            {/* Agent Info Bar */}
            <Box px={isMobile ? 'sm' : 'md'} py="xs" style={{ background:'#f8f9fa', borderBottom:'1px solid #e9ecef' }}>
              <Group gap="sm" justify="space-between">
                <Group gap="sm">
                  <Avatar color={avatarColor(agentName)} radius="xl">{getInitial(agentName)}</Avatar>
                  <Box>
                    <Text fw={600} size="sm">{agentName}</Text>
                    <Badge size="xs" color="green" variant="dot">{agentStatus === 'typing' ? 'typing...' : 'Online'}</Badge>
                  </Box>
                </Group>
                <Tooltip label="End conversation" position="bottom">
                  <ActionIcon variant="light" color="red" size="sm" onClick={handleEndChat}>
                    <IconX size={16}/>
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Box>

            {/* Messages */}
            <Box style={{ flex:1, overflowY:'auto', padding: isMobile ? '12px' : '16px', background:'#fafbfc' }}>
              <Stack gap={isMobile ? 'xs' : 'sm'}>
                {messages.map((msg, i) => {
                  if (msg.sender === 'system' || msg.isCallEnded) {
                    const isCallEnded = msg.isCallEnded || (msg.text && msg.text.includes('Call ended'));
                    return (
                      <Flex key={i} justify="center">
                        <Badge size="sm" variant="light" color={isCallEnded ? 'blue' : 'gray'}>
                          {msg.text}
                        </Badge>
                      </Flex>
                    );
                  }
                  const isUser = msg.sender === 'user';
                  return (
                    <Flex key={i} justify={isUser ? 'flex-end' : 'flex-start'} gap="xs" align="flex-end">
                      {!isUser && <Avatar size={isMobile ? 24 : 28} color={avatarColor(agentName)} radius="xl">{getInitial(agentName)}</Avatar>}
                      <Box style={{ maxWidth: isMobile ? '75%' : '70%' }}>
                        <Paper p={isMobile ? 'xs' : 'sm'} px={isMobile ? 'sm' : 'md'} style={{
                          background: isUser ? 'linear-gradient(135deg, #ff8c00 0%, #ffb347 100%)' : '#fff',
                          border: isUser ? 'none' : '1px solid #e9ecef',
                          borderRadius: isUser ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                          boxShadow: isUser ? '0 2px 8px rgba(255,140,0,0.3)' : 'none',
                        }}>
                          <Text size={isMobile ? 'sm' : 'md'} c={isUser ? '#2d2b3d' : 'dark.7'}>{msg.text}</Text>
                        </Paper>
                        <Text size="xs" c="dimmed" mt={2} ta={isUser?'right':'left'}>{formatTime(msg.timestamp)}</Text>
                      </Box>
                      {isUser && <Avatar size={isMobile ? 24 : 28} color={avatarColor(name)} radius="xl">{getInitial(name)}</Avatar>}
                    </Flex>
                  );
                })}
                <div ref={messagesEnd}/>
              </Stack>
            </Box>

            {/* Input */}
            <Box px={isMobile ? 'xs' : 'md'} py="xs" style={{ background:'#fff', borderTop:'1px solid #e9ecef' }}>
              <Group gap="xs" wrap="nowrap" align="center">
                <ActionIcon variant="subtle" color="gray" size="md"><IconPaperclip size={18}/></ActionIcon>
                <ActionIcon variant="subtle" color="gray" size="md"><IconMoodSmile size={18}/></ActionIcon>
                <TextInput
                  placeholder="Type your message..."
                  style={{ flex:1 }}
                  size="sm"
                  radius="xl"
                  value={input}
                  onChange={e=>handleInputChange(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&sendMessage()}
                />
                <ActionIcon size={isMobile ? 36 : 40} radius="xl" variant="gradient" gradient={{from:'#ff8c00',to:'#ffb347'}} onClick={sendMessage}>
                  <IconSend size={isMobile ? 14 : 16}/>
                </ActionIcon>
              </Group>
            </Box>
          </Flex>
        )}

        {/* Step 5: Closed */}
        {(step==='closed' || step==='rejected') && (
          <Flex direction="column" align="center" justify="center" p={isMobile ? 'md' : 'xl'} gap="lg" style={{ flex:1 }}>
            <Box style={{ width:80, height:80, borderRadius:'50%', background: step==='rejected' ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : 'linear-gradient(135deg, #ff8c00 0%, #ffb347 100%)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 10px 40px rgba(0,0,0,0.2)' }}>
              <IconCheck size={40} color="#2d2d2d"/>
            </Box>
            <Title order={3} ta="center">{step==='rejected' ? 'Request Dismissed' : 'Chat Ended'}</Title>
            <Text c="dimmed" ta="center" maw={300}>{closedMsg}</Text>

            {/* Rating UI - only show for closed chats, not rejected */}
            {step==='closed' && !ratingSubmitted && (
              <Stack align="center" gap="md" w="100%" mt="md">
                <Text size="sm" fw={600} c="dark.5">How was your experience?</Text>
                <Group gap="xs">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <UnstyledButton
                      key={star}
                      onClick={() => !ratingLoading && setRating(star)}
                      style={{ cursor: ratingLoading ? 'not-allowed' : 'pointer', fontSize: '28px', opacity: rating >= star ? 1 : 0.3, transition: 'opacity 0.2s' }}
                    >
                      ⭐
                    </UnstyledButton>
                  ))}
                </Group>
                
                {ratingError && (
                  <Text size="xs" c="red" ta="center" maw={240}>
                    {ratingError}
                  </Text>
                )}
                
                {rating > 0 && (
                  <>
                    <TextInput
                      placeholder="Additional feedback (optional)"
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      maxLength={200}
                      size="xs"
                      w="100%"
                      style={{ maxWidth: 240 }}
                      disabled={ratingLoading}
                    />
                    <Button 
                      size="xs" 
                      onClick={submitRating} 
                      variant="gradient" 
                      gradient={{ from: '#ff8c00', to: '#ffb347' }}
                      loading={ratingLoading}
                      disabled={ratingLoading}
                    >
                      {ratingLoading ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                  </>
                )}
              </Stack>
            )}

            {/* Thank you message after rating */}
            {ratingSubmitted && (
              <Text size="sm" c="green" fw={600}>Thank you for your feedback!</Text>
            )}

            <Stack w={isMobile ? '100%' : 250} gap="sm" mt="md">
              <Button fullWidth radius="md" leftSection={<IconRefresh size={16}/>} variant="gradient" gradient={{from:'#ff8c00',to:'#ffb347'}} onClick={reset}>
                Start New Chat
              </Button>
            </Stack>
          </Flex>
        )}
        </Box>
      </Paper>

      {/* Footer - Desktop only */}
      {!isMobile && (
        <Text ta="center" size="xs" c="#666666" mt="lg" style={{ position:'fixed', bottom:10 }}>
          Powered by RPRPrime Chat • Secure & Private
        </Text>
      )}

    {/* ── Incoming Call Modal ── */}
    {incomingCall && (
      <Modal opened onClose={rejectCall} withCloseButton={false} centered
        styles={{content:{background:'#ffffff',border:'1px solid #f0e8e0'}}}>
        <Stack align="center" gap="md" py="lg">
          <Avatar size={80} color="orange" radius={80} style={{boxShadow:'0 0 0 8px #f0e8e0'}}>
            <IconPhone size={40} />
          </Avatar>
          <Text fw={700} c="#2d2d2d" size="lg">Incoming {incomingCall.callType === 'video' ? 'Video' : 'Voice'} Call</Text>
          <Text c="#666666" size="sm">{incomingCall.from} is calling you</Text>
          <Group gap="md" mt="sm">
            <Button radius="xl" color="red" leftSection={<IconPhoneOff size={16}/>} onClick={rejectCall}>Decline</Button>
            <Button radius="xl" color="green" leftSection={<IconPhone size={16}/>} onClick={acceptCall}>Accept</Button>
          </Group>
        </Stack>
      </Modal>
    )}

    {/* ── Incoming Call Modal (Helpdesk Calling) ── */}
    {incomingCall && (
      <Modal opened onClose={rejectCall} withCloseButton={false} centered closeOnClickOutside={false}
        styles={{content:{background:'#ffffff',border:'1px solid #f0e8e0'}}}>
        <Stack align="center" gap="md" py="lg">
          <Box style={{
            width:100, height:100, borderRadius:'50%',
            background:'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:'0 0 0 12px rgba(245,87,108,0.15)',
            animation:'pulse 2s infinite'
          }}>
            {incomingCall.callType === 'video' ? <IconVideo size={48} color="white"/> : <IconPhone size={48} color="white"/>}
          </Box>
          <Title order={4} c="#2d2d2d" fw={700}>Incoming {incomingCall.callType === 'video' ? 'Video' : 'Audio'} Call</Title>
          <Text c="#666666" size="sm" ta="center">
            {incomingCall.from || 'Support Agent'} is calling you
          </Text>
          <Group gap="xl" mt="md">
            <ActionIcon size={60} radius="xl" color="red" variant="filled"
              onClick={rejectCall}
              style={{ boxShadow:'0 4px 20px rgba(255,0,0,0.4)' }}>
              <IconPhoneOff size={28} />
            </ActionIcon>
            <ActionIcon size={60} radius="xl" color="green" variant="filled"
              onClick={acceptCall}
              style={{ boxShadow:'0 4px 20px rgba(0,255,0,0.4)' }}>
              <IconPhone size={28} />
            </ActionIcon>
          </Group>
          <Text c="#666666" size="xs" mt="sm">
            Press accept to answer or reject to decline
          </Text>
        </Stack>
      </Modal>
    )}

    {/* Calling modal - hidden since customers cannot initiate calls */}
    {false && callStatus === 'calling' && callModal && (
      <Modal opened onClose={() => { setCallStatus(null); setCallModal(null); }} withCloseButton={false} centered
        styles={{content:{background:'#ffffff',border:'1px solid #f0e8e0'}}}>
        <Stack align="center" gap="md" py="lg">
          <Avatar size={80} color="orange" radius={80} style={{boxShadow:'0 0 0 8px #f0e8e0'}}>
            {callModal === 'video' ? <IconVideo size={40} /> : <IconPhone size={40} />}
          </Avatar>
          <Text fw={700} c="#2d2d2d" size="lg">Calling Support Agent...</Text>
          <Text c="#666666" size="sm">{callModal === 'video' ? 'Video' : 'Voice'} call in progress</Text>
          <Group gap="md" mt="sm">
            <Button radius="xl" color="red" leftSection={<IconPhoneOff size={16}/>}
              onClick={() => { setCallStatus(null); setCallModal(null); }}>Cancel</Button>
          </Group>
        </Stack>
      </Modal>
    )}

    {/* ── Active Call Modal ── */}
    {(callStatus === 'connected' || localStream) && (
      <Modal opened onClose={endCall} withCloseButton={false} centered size="lg"
        styles={{content:{background:'#ffffff',border:'1px solid #f0e8e0'},body:{padding:0}}}>
        <Stack align="center" gap="md" py="lg">
          {/* Hidden audio element to play remote audio */}
          <audio
            ref={audioRef => {
              if (audioRef && remoteStream) {
                audioRef.srcObject = remoteStream;
                const playPromise = audioRef.play();
                if (playPromise !== undefined) {
                  playPromise.catch(e => {
                    console.log('[WebRTC Client] Audio play error:', e.message);
                    // Try again after a short delay (browser autoplay policy)
                    setTimeout(() => {
                      if (audioRef.srcObject) {
                        audioRef.play().catch(e2 => console.log('[WebRTC Client] Retry audio play error:', e2.message));
                      }
                    }, 1000);
                  });
                }
              }
            }}
            autoPlay
            playsInline
          />

          {callModal === 'video' || incomingCall?.callType === 'video' ? (
            <Box style={{position:'relative',borderRadius:12,overflow:'hidden',background:'#000',aspectRatio:16/9,width:'100%'}}>
              {remoteStream ? (
                <video ref={v => v && (v.srcObject = remoteStream)} autoPlay playsInline style={{width:'100%',height:'100%',objectFit:'cover'}} />
              ) : (
                <Stack align="center" justify="center" h="100%">
                  <Text c="dimmed">Connecting...</Text>
                </Stack>
              )}
              {localStream && (
                <Box style={{position:'absolute',bottom:10,right:10,width:120,aspectRatio:16/9,borderRadius:8,overflow:'hidden',border:'2px solid #fff'}}>
                  <video ref={v => v && (v.srcObject = localStream)} autoPlay playsInline muted style={{width:'100%',height:'100%',objectFit:'cover',transform:'scaleX(-1)'}} />
                </Box>
              )}
            </Box>
          ) : (
            <Avatar size={100} color="green" radius={100}>
              <IconPhone size={50} />
            </Avatar>
          )}
          <Text fw={700} c="#2d2d2d" size="lg">Call in Progress</Text>
          <Text c={callStatus === 'connected' ? 'green' : '#6b6894'} size="sm">
            {callStatus === 'connected' ? '● Connected' : '● Connecting...'}
          </Text>
          {/* Microphone status indicator */}
          <Group gap="md" mt="sm">
            <Tooltip label={isMuted ? 'Unmute microphone' : 'Mute microphone'}>
              <ActionIcon
                size={40}
                radius="xl"
                variant="filled"
                color={isMuted ? 'red' : 'green'}
                onClick={() => {
                  if (localStream) {
                    const newMutedState = !isMuted;
                    localStream.getAudioTracks().forEach(track => {
                      track.enabled = !newMutedState; // If new state is muted, disable tracks
                    });
                    setIsMuted(newMutedState);
                  }
                }}
              >
                {isMuted ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}
              </ActionIcon>
            </Tooltip>
            {!isMuted && localStream && (
              <Badge color="green" variant="light" leftSection={<IconMicrophone size={12} />}>
                Microphone Active
              </Badge>
            )}
            {isMuted && (
              <Badge color="red" variant="light" leftSection={<IconMicrophoneOff size={12} />}>
                Microphone Muted
              </Badge>
            )}
          </Group>
          <Button radius="xl" color="red" leftSection={<IconPhoneOff size={16}/>} onClick={endCall} mt="md">
            End Call
          </Button>
        </Stack>
      </Modal>
    )}
  </Box>
);
}
